import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Assessment,
  Download,
  TrendingUp,
  TrendingDown,
  Schedule,
  Refresh,
  PictureAsPdf,
  TableChart,
  BarChart as BarChartIcon,
  Close,
  Visibility,
  ContentCopy,
  CalendarToday,
  ChevronRight,
  QrCode2,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { format, subDays, startOfMonth, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

interface BoletoStats {
  totalEmitido: number;
  totalRecebido: number;
  aReceber: number;
  vencidos: number;
  quantidadeEmitidos: number;
  quantidadePagos: number;
  quantidadeVencidos: number;
  quantidadeAbertos: number;
}

interface BoletoMovimento {
  data: string;
  emitidos: number;
  pagos: number;
  valorEmitido: number;
  valorPago: number;
  boletos?: Boleto[];
}

interface BoletoPorSituacao {
  situacao: string;
  quantidade: number;
  valor: number;
}

interface Boleto {
  nossoNumero: number;
  seuNumero?: string;
  linhaDigitavel?: string;
  codigoBarras?: string;
  situacao: number;
  dataEmissao?: string;
  dataVencimento: string;
  dataLimitePagamento?: string;
  valorNominal: number;
  valorAbatimento?: number;
  valorTotalRecebido?: number;
  dataPagamento?: string;
  pagador: {
    numeroCpfCnpj: string;
    nome: string;
    email?: string;
    endereco?: string;
    bairro?: string;
    cidade?: string;
    cep?: string;
    uf?: string;
  };
  mensagens?: {
    tipoInstrucao: number;
    mensagem: string;
  }[];
  pdfBoleto?: string;
  qrCode?: string;
}

const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336'];

const NOMES_PAGADORES = [
  'João Silva Santos',
  'Maria Oliveira Costa',
  'Pedro Henrique Souza',
  'Ana Carolina Lima',
  'Carlos Eduardo Ferreira',
  'Juliana Almeida',
  'Empresa ABC Ltda',
  'Comércio XYZ ME',
  'Tech Solutions S.A.',
  'Distribuidora Beta',
];

const BoletoReportsPage: React.FC = () => {
  const today = new Date();
  const firstDayOfMonth = startOfMonth(today);

  const [dataInicio, setDataInicio] = useState(format(firstDayOfMonth, 'yyyy-MM-dd'));
  const [dataFim, setDataFim] = useState(format(today, 'yyyy-MM-dd'));
  const [tipoGrafico, setTipoGrafico] = useState<'bar' | 'line'>('bar');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stats, setStats] = useState<BoletoStats>({
    totalEmitido: 0,
    totalRecebido: 0,
    aReceber: 0,
    vencidos: 0,
    quantidadeEmitidos: 0,
    quantidadePagos: 0,
    quantidadeVencidos: 0,
    quantidadeAbertos: 0,
  });

  const [movimentos, setMovimentos] = useState<BoletoMovimento[]>([]);
  const [porSituacao, setPorSituacao] = useState<BoletoPorSituacao[]>([]);

  // State para dialogs
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<BoletoMovimento | null>(null);
  const [boletoDetailOpen, setBoletoDetailOpen] = useState(false);
  const [selectedBoleto, setSelectedBoleto] = useState<Boleto | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatCpfCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const calculateInadimplencia = () => {
    if (stats.quantidadeEmitidos === 0) return 0;
    return (stats.quantidadeVencidos / stats.quantidadeEmitidos) * 100;
  };

  const generateBoletos = (data: string, quantidade: number, valorTotal: number): Boleto[] => {
    const boletos: Boleto[] = [];
    const valorMedio = valorTotal / quantidade;

    for (let i = 0; i < quantidade; i++) {
      const situacao = Math.random() > 0.4 ? 3 : Math.random() > 0.5 ? 1 : 2;
      const valorNominal = valorMedio * (0.5 + Math.random());
      const cpf = Math.floor(10000000000 + Math.random() * 89999999999).toString();

      boletos.push({
        nossoNumero: Math.floor(100000000 + Math.random() * 900000000),
        seuNumero: `PED-${Math.floor(1000 + Math.random() * 9000)}`,
        linhaDigitavel: `75691.${Math.floor(10000 + Math.random() * 90000)} ${Math.floor(10000 + Math.random() * 90000)}.${Math.floor(100000 + Math.random() * 900000)} ${Math.floor(10000 + Math.random() * 90000)}.${Math.floor(100000 + Math.random() * 900000)} 1 ${Math.floor(10000000000000 + Math.random() * 90000000000000)}`,
        codigoBarras: `7569${Math.floor(1000000000000000000000000000000000000000 + Math.random() * 9000000000000000000000000000000000000000)}`.slice(0, 44),
        situacao,
        dataEmissao: data,
        dataVencimento: format(subDays(parseISO(data), -10), 'yyyy-MM-dd'),
        dataLimitePagamento: format(subDays(parseISO(data), -15), 'yyyy-MM-dd'),
        valorNominal,
        valorTotalRecebido: situacao === 3 ? valorNominal : undefined,
        dataPagamento: situacao === 3 ? format(subDays(parseISO(data), -Math.floor(Math.random() * 10)), 'yyyy-MM-dd') : undefined,
        pagador: {
          numeroCpfCnpj: cpf,
          nome: NOMES_PAGADORES[Math.floor(Math.random() * NOMES_PAGADORES.length)],
          email: `cliente${i + 1}@email.com`,
          endereco: `Rua Exemplo, ${Math.floor(100 + Math.random() * 900)}`,
          bairro: 'Centro',
          cidade: 'São Paulo',
          uf: 'SP',
          cep: `${Math.floor(10000 + Math.random() * 90000)}-${Math.floor(100 + Math.random() * 900)}`,
        },
      });
    }

    return boletos;
  };

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const diasNoPeriodo = Math.ceil(
        (new Date(dataFim).getTime() - new Date(dataInicio).getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

      const movimentosSimulados: BoletoMovimento[] = [];
      let totalEmitido = 0;
      let totalRecebido = 0;
      let qtdEmitidos = 0;
      let qtdPagos = 0;

      for (let i = 0; i < Math.min(diasNoPeriodo, 30); i++) {
        const data = format(subDays(new Date(dataFim), diasNoPeriodo - 1 - i), 'yyyy-MM-dd');
        const emitidos = Math.floor(Math.random() * 10) + 1;
        const pagos = Math.floor(Math.random() * emitidos);
        const valorEmitido = (Math.random() * 5000 + 500) * emitidos;
        const valorPago = (Math.random() * 0.9 + 0.1) * valorEmitido;

        movimentosSimulados.push({
          data,
          emitidos,
          pagos,
          valorEmitido,
          valorPago,
          boletos: generateBoletos(data, emitidos, valorEmitido),
        });

        totalEmitido += valorEmitido;
        totalRecebido += valorPago;
        qtdEmitidos += emitidos;
        qtdPagos += pagos;
      }

      setMovimentos(movimentosSimulados);

      const vencidos = Math.floor((qtdEmitidos - qtdPagos) * 0.3);
      const abertos = qtdEmitidos - qtdPagos - vencidos;

      setStats({
        totalEmitido,
        totalRecebido,
        aReceber: totalEmitido - totalRecebido,
        vencidos: totalEmitido * 0.05,
        quantidadeEmitidos: qtdEmitidos,
        quantidadePagos: qtdPagos,
        quantidadeVencidos: vencidos,
        quantidadeAbertos: abertos,
      });

      setPorSituacao([
        { situacao: 'Liquidado', quantidade: qtdPagos, valor: totalRecebido },
        { situacao: 'Em Aberto', quantidade: abertos, valor: (totalEmitido - totalRecebido) * 0.7 },
        { situacao: 'Vencido', quantidade: vencidos, valor: (totalEmitido - totalRecebido) * 0.3 },
      ]);

    } catch (err: any) {
      console.error('Erro ao carregar relatório:', err);
      setError(err.response?.data?.error?.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const handleFiltrar = () => {
    fetchReportData();
  };

  const handleDayClick = (movimento: BoletoMovimento) => {
    setSelectedDay(movimento);
    setDayDetailOpen(true);
  };

  const handleBoletoClick = (boleto: Boleto) => {
    setSelectedBoleto(boleto);
    setBoletoDetailOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusChip = (situacaoCode: number) => {
    switch (situacaoCode) {
      case 1:
        return <Chip label="Em Aberto" color="warning" size="small" />;
      case 2:
        return <Chip label="Baixado" color="default" size="small" />;
      case 3:
        return <Chip label="Liquidado" color="success" size="small" />;
      default:
        return <Chip label={`Status ${situacaoCode}`} size="small" />;
    }
  };

  const exportToCSV = () => {
    const headers = ['Data', 'Emitidos', 'Pagos', 'Valor Emitido', 'Valor Pago'];
    const rows = movimentos.map(m => [
      format(parseISO(m.data), 'dd/MM/yyyy'),
      m.emitidos,
      m.pagos,
      m.valorEmitido.toFixed(2),
      m.valorPago.toFixed(2),
    ]);

    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_boletos_${dataInicio}_${dataFim}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2" fontWeight="medium">
            {format(parseISO(label), 'dd/MM/yyyy', { locale: ptBR })}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Valor') ? formatCurrency(entry.value) : entry.value}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Clique para ver detalhes
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const handleChartClick = (data: any) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedData = data.activePayload[0].payload;
      handleDayClick(clickedData);
    }
  };

  const statsCards = [
    {
      title: 'Total Emitido',
      value: formatCurrency(stats.totalEmitido),
      subtitle: `${stats.quantidadeEmitidos} boletos`,
      icon: <TrendingUp />,
      color: 'primary.main',
      bgColor: 'primary.light',
    },
    {
      title: 'Total Recebido',
      value: formatCurrency(stats.totalRecebido),
      subtitle: `${stats.quantidadePagos} boletos pagos`,
      icon: <TrendingUp />,
      color: 'success.main',
      bgColor: 'success.light',
    },
    {
      title: 'A Receber',
      value: formatCurrency(stats.aReceber),
      subtitle: `${stats.quantidadeAbertos} em aberto`,
      icon: <Schedule />,
      color: 'warning.main',
      bgColor: 'warning.light',
    },
    {
      title: 'Inadimplência',
      value: formatPercentage(calculateInadimplencia()),
      subtitle: `${stats.quantidadeVencidos} vencidos`,
      icon: <TrendingDown />,
      color: 'error.main',
      bgColor: 'error.light',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
          Relatórios de Boletos
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={handleFiltrar}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Aviso de dados simulados */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="medium">
          📊 Dados de Demonstração
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Os dados exibidos são simulados para demonstração. Para dados reais, configure o Webhook de boletos
          em <strong>Configurações → Webhooks</strong> ou utilize a API de Movimentação do Sicoob.
        </Typography>
      </Alert>

      {/* Cards de estatísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      {stat.title}
                    </Typography>
                    {loading ? (
                      <Skeleton width={100} height={40} />
                    ) : (
                      <Typography variant="h5" fontWeight="bold" sx={{ color: stat.color }}>
                        {stat.value}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {stat.subtitle}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: stat.bgColor,
                      color: stat.color,
                      opacity: 0.8,
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filtros e exportação */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Período e Exportação
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleFiltrar}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Filtrar'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<TableChart />}
              onClick={exportToCSV}
              disabled={loading || movimentos.length === 0}
            >
              Exportar CSV
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo Gráfico</InputLabel>
              <Select
                value={tipoGrafico}
                label="Tipo Gráfico"
                onChange={(e) => setTipoGrafico(e.target.value as 'bar' | 'line')}
              >
                <MenuItem value="bar">Barras</MenuItem>
                <MenuItem value="line">Linhas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Gráfico de evolução */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Evolução de Boletos
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (clique em uma barra para ver detalhes)
              </Typography>
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            ) : movimentos.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                {tipoGrafico === 'bar' ? (
                  <BarChart data={movimentos} onClick={handleChartClick} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="data"
                      tickFormatter={(value) => format(parseISO(value), 'dd/MM')}
                      fontSize={12}
                    />
                    <YAxis yAxisId="left" orientation="left" stroke="#2196f3" />
                    <YAxis yAxisId="right" orientation="right" stroke="#4caf50" />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="valorEmitido"
                      name="Valor Emitido"
                      fill="#2196f3"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="valorPago"
                      name="Valor Pago"
                      fill="#4caf50"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                ) : (
                  <LineChart data={movimentos} onClick={handleChartClick} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="data"
                      tickFormatter={(value) => format(parseISO(value), 'dd/MM')}
                      fontSize={12}
                    />
                    <YAxis yAxisId="left" orientation="left" stroke="#2196f3" />
                    <YAxis yAxisId="right" orientation="right" stroke="#4caf50" />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="valorEmitido"
                      name="Valor Emitido"
                      stroke="#2196f3"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="valorPago"
                      name="Valor Pago"
                      stroke="#4caf50"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <Typography color="text.secondary">
                  Nenhum dado disponível para o período selecionado
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Gráfico de pizza - distribuição por situação */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Distribuição por Situação
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            ) : porSituacao.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={porSituacao}
                      dataKey="quantidade"
                      nameKey="situacao"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ situacao, percent }) => `${situacao} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {porSituacao.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value: any, name: any, props: any) => [
                        `${value} boletos - ${formatCurrency(props.payload.valor)}`,
                        props.payload.situacao,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <Box sx={{ mt: 2 }}>
                  {porSituacao.map((item, index) => (
                    <Box
                      key={item.situacao}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: COLORS[index],
                          }}
                        />
                        <Typography variant="body2">{item.situacao}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {item.quantidade}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(item.valor)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <Typography color="text.secondary">Sem dados</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela de movimentação diária */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Movimentação Diária
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            (clique em uma linha para ver detalhes)
          </Typography>
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="center">Emitidos</TableCell>
                  <TableCell align="center">Pagos</TableCell>
                  <TableCell align="right">Valor Emitido</TableCell>
                  <TableCell align="right">Valor Recebido</TableCell>
                  <TableCell align="right">Taxa Conversão</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movimentos.length > 0 ? (
                  movimentos.map((mov) => (
                    <TableRow
                      key={mov.data}
                      hover
                      onClick={() => handleDayClick(mov)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday fontSize="small" color="action" />
                          {format(parseISO(mov.data), 'dd/MM/yyyy', { locale: ptBR })}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={mov.emitidos} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={mov.pagos} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(mov.valorEmitido)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(mov.valorPago)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${mov.emitidos > 0 ? ((mov.pagos / mov.emitidos) * 100).toFixed(0) : 0}%`}
                          size="small"
                          color={mov.pagos / mov.emitidos >= 0.7 ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver boletos do dia">
                          <IconButton size="small">
                            <ChevronRight fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        Nenhum dado disponível
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog - Detalhes do Dia */}
      <Dialog
        open={dayDetailOpen}
        onClose={() => setDayDetailOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
              Boletos do Dia
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedDay && format(parseISO(selectedDay.data), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </Typography>
          </Box>
          <IconButton onClick={() => setDayDetailOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDay && (
            <>
              {/* Resumo do dia */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Emitidos</Typography>
                      <Typography variant="h6" color="primary.main">{selectedDay.emitidos}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Pagos</Typography>
                      <Typography variant="h6" color="success.main">{selectedDay.pagos}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Valor Emitido</Typography>
                      <Typography variant="h6">{formatCurrency(selectedDay.valorEmitido)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Valor Pago</Typography>
                      <Typography variant="h6" color="success.main">{formatCurrency(selectedDay.valorPago)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Lista de boletos */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell>Nosso Número</TableCell>
                      <TableCell>Seu Número</TableCell>
                      <TableCell>Pagador</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell>Situação</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedDay.boletos && selectedDay.boletos.length > 0 ? (
                      selectedDay.boletos.map((boleto) => (
                        <TableRow key={boleto.nossoNumero} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {boleto.nossoNumero}
                            </Typography>
                          </TableCell>
                          <TableCell>{boleto.seuNumero || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{boleto.pagador.nome}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCpfCnpj(boleto.pagador.numeroCpfCnpj)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(boleto.valorNominal)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {format(parseISO(boleto.dataVencimento), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell>{getStatusChip(boleto.situacao)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBoletoClick(boleto);
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>
                            Nenhum boleto encontrado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayDetailOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog - Detalhes do Boleto */}
      <Dialog
        open={boletoDetailOpen}
        onClose={() => setBoletoDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Detalhes do Boleto</Typography>
            <Typography variant="caption" color="text.secondary">
              Nosso Número: {selectedBoleto?.nossoNumero}
            </Typography>
          </Box>
          <IconButton onClick={() => setBoletoDetailOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBoleto && (
            <Grid container spacing={3}>
              {/* Linha Digitável */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Linha Digitável
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontFamily="monospace" sx={{ flex: 1 }}>
                        {selectedBoleto.linhaDigitavel || 'Não disponível'}
                      </Typography>
                      {selectedBoleto.linhaDigitavel && (
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(selectedBoleto.linhaDigitavel!)}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Dados do Boleto */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Dados do Boleto
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Nosso Número:</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedBoleto.nossoNumero}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Seu Número:</Typography>
                        <Typography variant="body2">{selectedBoleto.seuNumero || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Data Emissão:</Typography>
                        <Typography variant="body2">
                          {selectedBoleto.dataEmissao ? format(parseISO(selectedBoleto.dataEmissao), 'dd/MM/yyyy') : '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Data Vencimento:</Typography>
                        <Typography variant="body2">
                          {format(parseISO(selectedBoleto.dataVencimento), 'dd/MM/yyyy')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Limite Pagamento:</Typography>
                        <Typography variant="body2">
                          {selectedBoleto.dataLimitePagamento ? format(parseISO(selectedBoleto.dataLimitePagamento), 'dd/MM/yyyy') : '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Situação:</Typography>
                        {getStatusChip(selectedBoleto.situacao)}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Valores */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Valores
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Valor Nominal:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(selectedBoleto.valorNominal)}
                        </Typography>
                      </Box>
                      {selectedBoleto.valorAbatimento && selectedBoleto.valorAbatimento > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Abatimento:</Typography>
                          <Typography variant="body2" color="error.main">
                            - {formatCurrency(selectedBoleto.valorAbatimento)}
                          </Typography>
                        </Box>
                      )}
                      {selectedBoleto.valorTotalRecebido && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Valor Recebido:</Typography>
                          <Typography variant="body2" color="success.main" fontWeight="medium">
                            {formatCurrency(selectedBoleto.valorTotalRecebido)}
                          </Typography>
                        </Box>
                      )}
                      {selectedBoleto.dataPagamento && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Data Pagamento:</Typography>
                          <Typography variant="body2" color="success.main">
                            {format(parseISO(selectedBoleto.dataPagamento), 'dd/MM/yyyy')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Dados do Pagador */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Dados do Pagador
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Nome:</Typography>
                        <Typography variant="body2">{selectedBoleto.pagador.nome}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">CPF/CNPJ:</Typography>
                        <Typography variant="body2">
                          {formatCpfCnpj(selectedBoleto.pagador.numeroCpfCnpj)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">E-mail:</Typography>
                        <Typography variant="body2">{selectedBoleto.pagador.email || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Endereço:</Typography>
                        <Typography variant="body2">
                          {selectedBoleto.pagador.endereco
                            ? `${selectedBoleto.pagador.endereco}, ${selectedBoleto.pagador.bairro || ''} - ${selectedBoleto.pagador.cidade || ''}/${selectedBoleto.pagador.uf || ''} - CEP: ${selectedBoleto.pagador.cep || ''}`
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Código de Barras */}
              {selectedBoleto.codigoBarras && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Código de Barras
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontFamily="monospace" sx={{ flex: 1 }}>
                          {selectedBoleto.codigoBarras}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(selectedBoleto.codigoBarras!)}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PictureAsPdf />}
            onClick={() => {
              // Download PDF seria implementado aqui
              alert('Funcionalidade de download PDF será implementada quando conectado à API real');
            }}
          >
            Download PDF
          </Button>
          <Button onClick={() => setBoletoDetailOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoletoReportsPage;
